import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  LogOut, Search, Download, Trash2, Users, FileText, Heart,
  ChevronLeft, ChevronRight, AlertCircle, RefreshCw,
  GraduationCap, DollarSign, BookOpen, Building2, BarChart3,
  Receipt, Banknote, Shield, Menu, X, ChevronDown, ChevronUp,
  UserCheck, CreditCard, Wallet, ClipboardList, PieChart,
  ScrollText, TrendingUp, BadgeDollarSign, HandCoins,
} from 'lucide-react';

// ── Role → allowed tables (mirrors api/lib/auth.js ROLE_TABLES) ──────────────

const ROLE_TABLES = {
  admin: new Set([
    'student_applications', 'student_admissions',
    'students', 'scholarships', 'fee_payments',
    'pledges', 'donations',
    'bank_accounts', 'accounts', 'budget_heads', 'expenses', 'transactions',
    'staff', 'salary_records', 'tax_records',
    'balance_sheet_entries', 'income_statement_entries',
    'users', 'user_roles', 'audit_logs',
  ]),
  accounts: new Set([
    'bank_accounts', 'accounts', 'budget_heads', 'expenses', 'transactions',
    'staff', 'salary_records', 'tax_records',
    'balance_sheet_entries', 'income_statement_entries',
    'audit_logs',
  ]),
  student: new Set([
    'student_applications', 'student_admissions',
    'students', 'scholarships', 'fee_payments',
  ]),
  payments: new Set([
    'pledges', 'donations', 'fee_payments', 'transactions', 'bank_accounts',
  ]),
};

const ROLE_BADGE = {
  admin:    { label: 'Administrator',    cls: 'bg-purple-100 text-purple-700' },
  accounts: { label: 'Accounts Manager', cls: 'bg-blue-100 text-blue-700'   },
  student:  { label: 'Student Services', cls: 'bg-emerald-100 text-emerald-700' },
  payments: { label: 'Payments Officer', cls: 'bg-amber-100 text-amber-700' },
};

// ── Table Groups & Config ────────────────────────────────────────────────────

const TABLE_GROUPS = [
  {
    label: 'Applications',
    icon: ClipboardList,
    color: 'text-brand-indigo',
    bg: 'bg-brand-indigo/10',
    tables: ['student_applications', 'student_admissions'],
  },
  {
    label: 'Students',
    icon: GraduationCap,
    color: 'text-brand-emerald',
    bg: 'bg-brand-emerald/10',
    tables: ['students', 'scholarships', 'fee_payments'],
  },
  {
    label: 'Fundraising',
    icon: HandCoins,
    color: 'text-brand-rose',
    bg: 'bg-brand-rose/10',
    tables: ['pledges', 'donations'],
  },
  {
    label: 'Finance',
    icon: Wallet,
    color: 'text-brand-amber',
    bg: 'bg-brand-amber/10',
    tables: ['bank_accounts', 'accounts', 'budget_heads', 'expenses', 'transactions'],
  },
  {
    label: 'Payroll & Tax',
    icon: BadgeDollarSign,
    color: 'text-brand-indigo',
    bg: 'bg-brand-indigo/10',
    tables: ['staff', 'salary_records', 'tax_records'],
  },
  {
    label: 'Reports',
    icon: TrendingUp,
    color: 'text-brand-emerald',
    bg: 'bg-brand-emerald/10',
    tables: ['balance_sheet_entries', 'income_statement_entries'],
  },
  {
    label: 'System',
    icon: Shield,
    color: 'text-brand-slate',
    bg: 'bg-brand-slate/10',
    tables: ['users', 'user_roles', 'audit_logs'],
  },
];

const TABLE_CONFIG = {
  // ── Applications ──────────────────────────────────────────────────────────
  student_applications: {
    name: 'Student Applications',
    icon: FileText,
    columns: [
      { key: 'full_name',        label: 'Full Name' },
      { key: 'email',            label: 'Email' },
      { key: 'phone',            label: 'Phone' },
      { key: 'current_class',    label: 'Class' },
      { key: 'city',             label: 'City' },
      { key: 'status',           label: 'Status', type: 'badge' },
      { key: 'created_at',       label: 'Applied', type: 'date' },
    ],
  },
  student_admissions: {
    name: 'Student Admissions',
    icon: Users,
    columns: [
      { key: 'full_name',        label: 'Full Name' },
      { key: 'email_address',    label: 'Email' },
      { key: 'mobile_number',    label: 'Mobile' },
      { key: 'preferred_course', label: 'Course' },
      { key: 'admission_status', label: 'Status', type: 'badge' },
      { key: 'enrolled',         label: 'Enrolled', type: 'boolean' },
      { key: 'created_at',       label: 'Date', type: 'date' },
    ],
  },
  // ── Students ─────────────────────────────────────────────────────────────
  students: {
    name: 'Students',
    icon: GraduationCap,
    columns: [
      { key: 'student_code',  label: 'Code' },
      { key: 'full_name',     label: 'Name' },
      { key: 'email',         label: 'Email' },
      { key: 'program',       label: 'Program' },
      { key: 'academic_year', label: 'Year' },
      { key: 'status',        label: 'Status', type: 'badge' },
      { key: 'enrolled_at',   label: 'Enrolled', type: 'date' },
    ],
  },
  scholarships: {
    name: 'Scholarships',
    icon: BookOpen,
    columns: [
      { key: 'scholarship_code', label: 'Code' },
      { key: 'scholarship_type', label: 'Type' },
      { key: 'academic_year',    label: 'Year' },
      { key: 'amount',           label: 'Amount', type: 'currency' },
      { key: 'status',           label: 'Status', type: 'badge' },
      { key: 'awarded_at',       label: 'Awarded', type: 'date' },
    ],
  },
  fee_payments: {
    name: 'Fee Payments',
    icon: Receipt,
    columns: [
      { key: 'receipt_number',  label: 'Receipt #' },
      { key: 'fee_type',        label: 'Type' },
      { key: 'amount',          label: 'Amount', type: 'currency' },
      { key: 'payment_method',  label: 'Method' },
      { key: 'status',          label: 'Status', type: 'badge' },
      { key: 'payment_date',    label: 'Date', type: 'date' },
    ],
  },
  // ── Fundraising ───────────────────────────────────────────────────────────
  pledges: {
    name: 'Pledges',
    icon: Heart,
    columns: [
      { key: 'name',    label: 'Sponsor' },
      { key: 'email',   label: 'Email' },
      { key: 'amount',  label: 'Amount', type: 'currency' },
      { key: 'currency',label: 'Currency' },
      { key: 'status',  label: 'Status', type: 'badge' },
      { key: 'created_at', label: 'Date', type: 'date' },
    ],
  },
  donations: {
    name: 'Donations',
    icon: HandCoins,
    columns: [
      { key: 'donation_code',  label: 'Code' },
      { key: 'donor_name',     label: 'Donor' },
      { key: 'donor_email',    label: 'Email' },
      { key: 'amount',         label: 'Amount', type: 'currency' },
      { key: 'donation_type',  label: 'Type' },
      { key: 'status',         label: 'Status', type: 'badge' },
      { key: 'donation_date',  label: 'Date', type: 'date' },
    ],
  },
  // ── Finance ───────────────────────────────────────────────────────────────
  bank_accounts: {
    name: 'Bank Accounts',
    icon: Building2,
    columns: [
      { key: 'account_name',    label: 'Account' },
      { key: 'bank_name',       label: 'Bank' },
      { key: 'account_number',  label: 'Number' },
      { key: 'account_type',    label: 'Type' },
      { key: 'current_balance', label: 'Balance', type: 'currency' },
      { key: 'is_primary',      label: 'Primary', type: 'boolean' },
      { key: 'is_active',       label: 'Active', type: 'boolean' },
    ],
  },
  accounts: {
    name: 'Chart of Accounts',
    icon: PieChart,
    columns: [
      { key: 'account_code', label: 'Code' },
      { key: 'account_name', label: 'Name' },
      { key: 'account_type', label: 'Type' },
      { key: 'balance',      label: 'Balance', type: 'currency' },
      { key: 'is_active',    label: 'Active', type: 'boolean' },
    ],
  },
  budget_heads: {
    name: 'Budget Heads',
    icon: BarChart3,
    columns: [
      { key: 'financial_year',   label: 'FY' },
      { key: 'category',         label: 'Category' },
      { key: 'sub_category',     label: 'Sub-Category' },
      { key: 'allocated_amount', label: 'Allocated', type: 'currency' },
      { key: 'spent_amount',     label: 'Spent', type: 'currency' },
      { key: 'is_active',        label: 'Active', type: 'boolean' },
    ],
  },
  expenses: {
    name: 'Expenses',
    icon: CreditCard,
    columns: [
      { key: 'expense_code',   label: 'Code' },
      { key: 'category',       label: 'Category' },
      { key: 'description',    label: 'Description' },
      { key: 'amount',         label: 'Amount', type: 'currency' },
      { key: 'payment_method', label: 'Method' },
      { key: 'status',         label: 'Status', type: 'badge' },
      { key: 'expense_date',   label: 'Date', type: 'date' },
    ],
  },
  transactions: {
    name: 'Transactions',
    icon: Banknote,
    columns: [
      { key: 'reference_number',  label: 'Reference' },
      { key: 'description',       label: 'Description' },
      { key: 'transaction_type',  label: 'Type', type: 'badge' },
      { key: 'amount',            label: 'Amount', type: 'currency' },
      { key: 'category',          label: 'Category' },
      { key: 'reconciled',        label: 'Reconciled', type: 'boolean' },
      { key: 'transaction_date',  label: 'Date', type: 'date' },
    ],
  },
  // ── Payroll ───────────────────────────────────────────────────────────────
  staff: {
    name: 'Staff',
    icon: UserCheck,
    columns: [
      { key: 'employee_code',   label: 'Code' },
      { key: 'full_name',       label: 'Name' },
      { key: 'designation',     label: 'Designation' },
      { key: 'department',      label: 'Department' },
      { key: 'basic_salary',    label: 'Basic Salary', type: 'currency' },
      { key: 'employment_type', label: 'Type' },
      { key: 'is_active',       label: 'Active', type: 'boolean' },
    ],
  },
  salary_records: {
    name: 'Salary Records',
    icon: DollarSign,
    columns: [
      { key: 'month',         label: 'Month' },
      { key: 'year',          label: 'Year' },
      { key: 'gross_salary',  label: 'Gross', type: 'currency' },
      { key: 'total_deductions', label: 'Deductions', type: 'currency' },
      { key: 'net_salary',    label: 'Net', type: 'currency' },
      { key: 'status',        label: 'Status', type: 'badge' },
      { key: 'payment_date',  label: 'Paid On', type: 'date' },
    ],
  },
  tax_records: {
    name: 'Tax Records',
    icon: ScrollText,
    columns: [
      { key: 'tax_type',       label: 'Type' },
      { key: 'financial_year', label: 'FY' },
      { key: 'period',         label: 'Period' },
      { key: 'tax_amount',     label: 'Tax', type: 'currency' },
      { key: 'total_amount',   label: 'Total', type: 'currency' },
      { key: 'status',         label: 'Status', type: 'badge' },
      { key: 'due_date',       label: 'Due', type: 'date' },
    ],
  },
  // ── Reports ───────────────────────────────────────────────────────────────
  balance_sheet_entries: {
    name: 'Balance Sheets',
    icon: BarChart3,
    columns: [
      { key: 'period_label',    label: 'Period' },
      { key: 'period_type',     label: 'Type' },
      { key: 'total_assets',    label: 'Assets', type: 'currency' },
      { key: 'total_liabilities',label: 'Liabilities', type: 'currency' },
      { key: 'total_equity',    label: 'Equity', type: 'currency' },
      { key: 'status',          label: 'Status', type: 'badge' },
    ],
  },
  income_statement_entries: {
    name: 'Income Statements',
    icon: TrendingUp,
    columns: [
      { key: 'period_label',  label: 'Period' },
      { key: 'period_type',   label: 'Type' },
      { key: 'total_income',  label: 'Income', type: 'currency' },
      { key: 'total_expense', label: 'Expenses', type: 'currency' },
      { key: 'net_surplus',   label: 'Net Surplus', type: 'currency' },
      { key: 'status',        label: 'Status', type: 'badge' },
    ],
  },
  // ── System ────────────────────────────────────────────────────────────────
  users: {
    name: 'Users',
    icon: Users,
    columns: [
      { key: 'email',      label: 'Email' },
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name',  label: 'Last Name' },
      { key: 'role',       label: 'Role', type: 'badge' },
      { key: 'is_active',  label: 'Active', type: 'boolean' },
      { key: 'last_login', label: 'Last Login', type: 'date' },
      { key: 'created_at', label: 'Created', type: 'date' },
    ],
  },
  user_roles: {
    name: 'User Roles',
    icon: Shield,
    columns: [
      { key: 'email',      label: 'Email' },
      { key: 'role',       label: 'Role', type: 'badge' },
      { key: 'is_active',  label: 'Active', type: 'boolean' },
      { key: 'created_at', label: 'Created', type: 'date' },
    ],
  },
  audit_logs: {
    name: 'Audit Logs',
    icon: ScrollText,
    columns: [
      { key: 'action',     label: 'Action', type: 'badge' },
      { key: 'module',     label: 'Module' },
      { key: 'ip_address', label: 'IP Address' },
      { key: 'user_agent', label: 'User Agent' },
      { key: 'created_at', label: 'Time', type: 'date' },
    ],
  },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const BADGE_COLORS = {
  // status
  active: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
  paid: 'bg-emerald-100 text-emerald-700',
  received: 'bg-emerald-100 text-emerald-700',
  fulfilled: 'bg-emerald-100 text-emerald-700',
  filed: 'bg-emerald-100 text-emerald-700',
  credit: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  offered: 'bg-blue-100 text-blue-700',
  accepted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  draft: 'bg-slate-100 text-slate-600',
  debit: 'bg-red-100 text-red-700',
  admin: 'bg-purple-100 text-purple-700',
  accounts: 'bg-blue-100 text-blue-700',
  staff: 'bg-indigo-100 text-indigo-700',
  CREATE: 'bg-emerald-100 text-emerald-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
};

const formatValue = (value, type) => {
  if (value === null || value === undefined || value === '') return '—';

  switch (type) {
    case 'date':
      try {
        return new Date(value).toLocaleDateString('en-IN', {
          year: 'numeric', month: 'short', day: 'numeric',
        });
      } catch { return String(value); }
    case 'currency':
      return `₹${parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    case 'boolean':
      return value ? '✓' : '✗';
    case 'badge':
      return value;
    default: {
      const s = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    }
  }
};

const BadgeCell = ({ value }) => {
  const cls = BADGE_COLORS[value] || 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {value}
    </span>
  );
};

const exportToCSV = (data, tableName) => {
  if (!data?.length) { alert('No data to export'); return; }
  const headers = Object.keys(data[0]);
  const csv = [
    headers.join(','),
    ...data.map(row =>
      headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')
    ),
  ].join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  a.download = `${tableName}_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const useDebounce = (value, delay) => {
  const [dv, setDv] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDv(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return dv;
};

const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('admin_token');
  if (!token) throw new Error('No authentication token. Please login again.');

  const response = await fetch(endpoint, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
  });

  if (response.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.reload();
    throw new Error('Session expired.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(`Server returned non-JSON (${response.status}): ${text.slice(0, 120)}`);
  }

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || data.message || `Request failed (${response.status})`);
  return data;
};

// ── Sub-components ───────────────────────────────────────────────────────────

const DashboardHeader = ({ onLogout, userName, userRole, sidebarOpen, setSidebarOpen }) => {
  const roleBadge = ROLE_BADGE[userRole];
  return (
    <header className="bg-background/90 backdrop-blur-md border-b border-border sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 py-3 gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(o => !o)}
            className="lg:hidden p-2 rounded-lg hover:bg-primary/10 text-foreground"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-logo">
              <span className="gradient-text">Admin Dashboard</span>
            </h1>
            <p className="text-xs text-muted-foreground hidden sm:block">Meenamma Educational Trust</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {roleBadge && (
            <span className={`hidden sm:inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${roleBadge.cls}`}>
              {roleBadge.label}
            </span>
          )}
          {userName && <span className="hidden md:block text-sm text-muted-foreground">{userName}</span>}
          <Button variant="outline" size="sm" onClick={onLogout} className="border-primary/30 text-primary hover:bg-primary/10">
            <LogOut className="h-4 w-4 mr-1.5" />
            Logout
          </Button>
        </div>
      </div>
    </header>
  );
};

const Sidebar = ({ activeTable, setActiveTable, sidebarOpen, setSidebarOpen, userRole }) => {
  const allowedTables = ROLE_TABLES[userRole] ?? ROLE_TABLES.admin;

  // Compute visible groups (only groups with ≥1 accessible table)
  const visibleGroups = TABLE_GROUPS.map(g => ({
    ...g,
    tables: g.tables.filter(t => allowedTables.has(t)),
  })).filter(g => g.tables.length > 0);

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    visibleGroups.forEach(g => { initial[g.label] = true; });
    return initial;
  });

  const toggleGroup = (label) => setOpenGroups(prev => ({ ...prev, [label]: !prev[label] }));

  const handleSelect = (tableKey) => {
    setActiveTable(tableKey);
    setSidebarOpen(false);
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-20
        w-60 bg-background border-r border-border
        flex flex-col overflow-hidden
        transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex-1 overflow-y-auto py-4 px-2">
          {visibleGroups.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = openGroups[group.label] ?? true;
            return (
              <div key={group.label} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <GroupIcon className={`h-3.5 w-3.5 ${group.color}`} />
                    {group.label}
                  </span>
                  {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>

                {isOpen && (
                  <div className="mt-0.5 space-y-0.5">
                    {group.tables.map((tableKey) => {
                      const cfg = TABLE_CONFIG[tableKey];
                      if (!cfg) return null;
                      const Icon = cfg.icon;
                      const isActive = activeTable === tableKey;
                      return (
                        <button
                          key={tableKey}
                          onClick={() => handleSelect(tableKey)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all text-left ${
                            isActive
                              ? 'bg-primary text-primary-foreground font-medium shadow-sm'
                              : 'text-foreground hover:bg-muted/60'
                          }`}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{cfg.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </>
  );
};

const DataTable = ({ data, config, onDelete, isDeleting }) => {
  if (!data?.length) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
        <p className="text-sm">No records found</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            {config.columns.map((col) => (
              <th key={col.key} className="text-left px-4 py-3 font-semibold text-foreground whitespace-nowrap text-xs uppercase tracking-wide">
                {col.label}
              </th>
            ))}
            <th className="text-right px-4 py-3 font-semibold text-foreground text-xs uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody>
          <AnimatePresence>
            {data.map((row, idx) => {
              const key = row.id || `${idx}`;
              return (
                <motion.tr
                  key={key}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                >
                  {config.columns.map((col) => (
                    <td key={col.key} className="px-4 py-3 text-muted-foreground">
                      {col.type === 'badge'
                        ? <BadgeCell value={row[col.key]} />
                        : formatValue(row[col.key], col.type)
                      }
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => onDelete(row.id, key)}
                      disabled={isDeleting || !row.id}
                      title={!row.id ? 'No ID available' : 'Delete record'}
                      className="p-1.5 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-30 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </motion.tr>
              );
            })}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
};

const DeleteModal = ({ isOpen, onConfirm, onCancel, isDeleting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-background rounded-2xl shadow-2xl p-6 max-w-sm w-full border border-border"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="p-2 bg-red-100 rounded-full">
            <AlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-base mb-1">Delete Record?</h3>
            <p className="text-sm text-muted-foreground">This action cannot be undone. Records with dependencies may fail to delete.</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isDeleting}>Cancel</Button>
          <Button size="sm" onClick={onConfirm} disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700 text-white border-0">
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const AdminDashboard = ({ session, onLogout }) => {
  const userRole = session?.user?.role || 'admin';
  const allowedTables = ROLE_TABLES[userRole] ?? ROLE_TABLES.admin;

  // Pick the first accessible table as the default
  const defaultTable = (() => {
    for (const group of TABLE_GROUPS) {
      for (const t of group.tables) {
        if (allowedTables.has(t)) return t;
      }
    }
    return 'student_applications';
  })();

  const [activeTable, setActiveTable] = useState(defaultTable);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [error, setError] = useState('');

  const ITEMS_PER_PAGE = 25;
  const debouncedSearch = useDebounce(searchTerm, 300);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const result = await apiCall(`/api/admin/tables?table=${activeTable}&limit=500`);
      setData(result.data || []);
      setTotalCount(result.total ?? result.data?.length ?? 0);
    } catch (err) {
      setError(err.message);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTable]);

  useEffect(() => {
    fetchData();
    setSearchTerm('');
    setCurrentPage(1);
  }, [activeTable, fetchData]);

  useEffect(() => { setCurrentPage(1); }, [debouncedSearch]);

  const filteredData = useMemo(() => {
    if (!debouncedSearch) return data;
    return data.filter(row =>
      Object.values(row).some(v => String(v ?? '').toLowerCase().includes(debouncedSearch.toLowerCase()))
    );
  }, [data, debouncedSearch]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const pageData = useMemo(() =>
    filteredData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredData, currentPage]
  );

  const openDelete = useCallback((id) => {
    if (!id) { setError('Cannot delete: record has no ID'); return; }
    setDeleteModal({ open: true, id });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteModal.id) return;
    setIsDeleting(true);
    try {
      await apiCall(`/api/admin/delete?table=${activeTable}&id=${deleteModal.id}`, { method: 'DELETE' });
      setDeleteModal({ open: false, id: null });
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal, activeTable, fetchData]);

  const config = TABLE_CONFIG[activeTable];

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col">
      <DashboardHeader
        onLogout={onLogout}
        userName={session?.user?.name || session?.user?.email}
        userRole={userRole}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          activeTable={activeTable}
          setActiveTable={setActiveTable}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          userRole={userRole}
        />

        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {/* Error banner */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 flex-1">{error}</p>
                <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 text-lg leading-none">×</button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Page header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              {config && (
                <div className="flex items-center gap-2">
                  {React.createElement(config.icon, { className: 'h-5 w-5 text-primary' })}
                  <h2 className="text-lg font-bold text-foreground">{config.name}</h2>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                    {totalCount} records
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search records…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}
                className="border-border text-foreground hover:bg-muted/50">
                <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={() => exportToCSV(filteredData, activeTable)}
                className="border-border text-foreground hover:bg-muted/50">
                <Download className="h-4 w-4 mr-1.5" />
                CSV
              </Button>
            </div>
          </div>

          {/* Table card */}
          <div className="bg-background rounded-2xl border border-border shadow-sm overflow-hidden">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                <div className="w-7 h-7 border-4 border-primary border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm">Loading {config?.name}…</p>
              </div>
            ) : (
              <DataTable
                data={pageData}
                config={config}
                onDelete={openDelete}
                isDeleting={isDeleting}
              />
            )}

            {/* Pagination + stats footer */}
            {!isLoading && filteredData.length > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50 bg-muted/10">
                <p className="text-xs text-muted-foreground">
                  {pageData.length} of {filteredData.length} shown
                  {debouncedSearch && ` (filtered from ${data.length})`}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-xs px-2 text-muted-foreground">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      <DeleteModal
        isOpen={deleteModal.open}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ open: false, id: null })}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default AdminDashboard;
